import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import SystemAccessDenied from './SystemAccessDenied';

// Public destination for a confirmed access denial (P5-8). Rendered as a
// sibling route to the MsalAuthenticationTemplate-protected tree in index.js,
// NOT inside it - if it were inside MsalAuthenticationTemplate, landing here
// straight after the logout triggered by accessDenialProcedure.js would find
// the user no longer authenticated and immediately bounce them back into an
// interactive Microsoft login, recreating the exact redirect loop this route
// exists to avoid (P5-9). It intentionally does not re-run the access
// bootstrap (currentUserAccess) on load.
const PublicAccessDeniedRoute = () => {
    const { instance, accounts } = useMsal();
    const [searchParams] = useSearchParams();
    const reasonCode = searchParams.get('reason') || null;
    // Real backend correlation ID carried through the logout redirect's query
    // string (accessDenialProcedure.js) - falls back to SystemAccessDenied's
    // own client-generated breadcrumb if reached without one (e.g. bookmarked).
    const correlationId = searchParams.get('correlationId') || null;

    // By the time a user reaches this route through the normal flow, App.js's
    // denial procedure has already cleared the local MSAL session - there is
    // no active account left, and nothing to sign out of. This button exists
    // for the one other case: the route reached directly/bookmarked while
    // still signed in. See accessDenialProcedure.js's file header for why
    // this clears the local session only (msalInstance.clearCache(), no IdP
    // round-trip) rather than calling logoutRedirect - every IdP-level
    // logout parameter tried (account/logoutHint/idTokenHint) still showed
    // Microsoft's "Pick an account" chooser on this tenant regardless.
    const handleSignOut = async () => {
        if (accounts && accounts.length > 0) {
            try {
                await instance.clearCache({ account: accounts[0] });
            } catch (err) {
                // Not fatal - still navigate away below regardless.
            }
        }
        window.location.assign('/');
    };

    const signOutLabel = accounts && accounts.length > 0 ? 'Sign out' : 'Return to sign-in';

    return (
        <SystemAccessDenied
            reasonCode={reasonCode}
            correlationId={correlationId}
            onSignOut={handleSignOut}
            signOutLabel={signOutLabel}
        />
    );
};

export default PublicAccessDeniedRoute;
